import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/site/Layout";
import { EventEmptyState } from "@/components/site/EventEmptyState";
import { Calendar, MapPin } from "lucide-react";
import { useSelectedEvent } from "@/lib/event-context";
import { resolveEventImageUrl, formatEventDateRange } from "@/lib/events";

export const Route = createFileRoute("/venue")({
  component: Venue,
  head: () => ({
    meta: [
      { title: "Venue — Arab Capital Markets Conference" },
      { name: "description", content: "Conference venue and location details in Doha, Qatar." },
      { property: "og:title", content: "Venue — Doha, Qatar" },
      { property: "og:description", content: "Location and travel details for delegates." },
    ],
  }),
});

function Venue() {
  const { selectedEvent, loading } = useSelectedEvent();

  if (loading) {
    return (
      <>
        <PageHero
          eyebrow="The Venue"
          title="Venue"
          subtitle="Location and travel details for the selected event"
        />
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center text-muted-foreground">Loading...</div>
        </section>
      </>
    );
  }

  if (!selectedEvent) {
    return (
      <>
        <PageHero eyebrow="The Venue" title="Venue" />
        <EventEmptyState title="Choose an event" message="Select an event in the header to see its venue details." />
      </>
    );
  }

  const venueDescription = selectedEvent.venue_description || "Delegates will gather at a premier venue selected for this conference. Additional travel and accommodation information will be shared with registered attendees.";
  const mapEmbedUrl = selectedEvent.map_embed_url || "https://www.openstreetmap.org/export/embed.html?bbox=51.45%2C25.25%2C51.6%2C25.35&layer=mapnik";
  const eventLocation = selectedEvent.location || "Venue to be confirmed";
  const eventDate = formatEventDateRange(selectedEvent) || "Date to be confirmed";
  const hotelName = selectedEvent.hotel_name || null;
  const phone = selectedEvent.phone || null;
  const address = selectedEvent.address || null;
  const directionsLink = selectedEvent.directions_link || null;
  const venueImageUrl = selectedEvent.venue_image_url
    ? resolveEventImageUrl(selectedEvent.venue_image_url)
    : null;
  const directions = selectedEvent.directions || null;

  return (
    <>
        <PageHero
          eyebrow="The Venue"
          title={eventLocation}
          subtitle={selectedEvent.title}
        />
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-12">
          {venueImageUrl && (
            <div className="w-full rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={venueImageUrl}
                alt="Venue"
                className="w-full h-auto object-cover"
              />
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-primary mb-4 sm:mb-5 leading-tight">
                Hosted in the heart of {eventLocation}
              </h2>
              <div
                className="text-muted-foreground leading-relaxed mb-5 sm:mb-6 text-sm sm:text-base prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: venueDescription }}
              />
              <ul className="space-y-3 text-sm sm:text-base">
                <li className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-accent shrink-0" /> {eventLocation}
                </li>
                <li className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-accent shrink-0" /> {eventDate}
                </li>
                {hotelName && (
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 text-accent shrink-0">🏨</span>
                    <span>{hotelName}</span>
                  </li>
                )}
                {phone && (
                  <li className="flex items-center gap-3">
                    <span className="w-5 h-5 text-accent shrink-0">📞</span>
                    <span>Tel: {phone}</span>
                  </li>
                )}
                {address && (
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 text-accent shrink-0">📍</span>
                    <div
                      className="break-words prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: address }}
                    />
                  </li>
                )}
                {directionsLink && (
                  <li className="flex items-center gap-3">
                    <span className="w-5 h-5 text-accent shrink-0">🗺️</span>
                    <a
                      href={directionsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Maps & Directions: Click Here
                    </a>
                  </li>
                )}
                {directions && (
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 text-accent shrink-0">🧭</span>
                    <div
                      className="break-words prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: directions }}
                    />
                  </li>
                )}
              </ul>
            </div>
            <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden shadow-2xl order-first md:order-last">
              <iframe
                title="Doha Map"
                src={mapEmbedUrl}
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
